import { DashboardState, EDatasourceType, ESdChangeReason, IActionDefinitionDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { getProcessUsedActionIds, prefetchProcessUsedActionIds } from "../../../shared/utility/process-action-usage";
import { NoticeBanner } from "../../common/notice-banner";
import { resolveTableEditorSqlDialect } from "../../table-editor/table-editor-dialect";
import { ITableRowsSwitcherItem, TableRowsEditor } from "../../table-editor/table-rows-editor";
import { AiHub } from "./ai-hub";
import { CreateAction } from "./create-action";
import { CreateSapAction } from "./create-action-sap";
import { EditSapAction } from "./create-action-sap-edit";
import { CreateAiAction } from "./create-ai-action";
import { CreateAIAgentAction } from "./create-ai-agent";
import { CreateBlocklyAction } from "./create-blockly-action";
import { CreateConnectionBluetooth } from "./create-connection-bluetooth";
import { CreateConnectionCsharp } from "./create-connection-csharp";
import { CreateConnectionMqtt } from "./create-connection-mqtt";
import { CreateConnectionMssql } from "./create-connection-mssql";
import { CreateConnectionOData } from "./create-connection-odata";
import { CreateConnectionOracle } from "./create-connection-oracle";
import { CreateConnectionPostgresql } from "./create-connection-postgresql";
import { CreateConnectionRestapi } from "./create-connection-restapi";
import { CreateRestApiCollection } from "./create-connection-restapi-wo-swagger-collection";
import { CreateRestApiEndpoint } from "./create-connection-restapi-wo-swagger-endpoint";
import { CreateConnectionSAP } from "./create-connection-sap";
import { CreateGoAction } from "./create-go-action";
import { CreateJavaAction } from "./create-java-action";
import { CreateJavaScriptAction } from "./create-javascript-action";
import { CreatePythonAction } from "./create-python-action";
import { CreateRustAction } from "./create-rust-action";
import { CreateSessionVariable } from "./create-session-variable";
import { CreateTable } from "./create-table";
import { DatasourcesLanding } from "./datasources-landing";
import { getDatasourceDisplayName } from "./datasources-landing-model";
import { buildRowBrowserSwitcherItems } from "./editor-switcher/editor-switcher-items";
import { CreateVisionAction } from "./vision-action/create-vision-action";

export interface IDataDiagramCanvasState {
  usedActions: IActionDefinitionDto[];
  /** Which notice the user dismissed, keyed by the editor subject it belongs to, so opening a
      different action or datasource shows the warning again. */
  dismissedNoticeKey?: string;
}

export interface IDataDiagramCanvasProps {}

class DataDiagramCanvas extends PureSdComponent<IDataDiagramCanvasProps & IWithSDProps, IDataDiagramCanvasState> {
  constructor(props: IDataDiagramCanvasProps & IWithSDProps) {
    super(props);
    this.state = {
      usedActions: []
    };
  }

  componentDidMount() {
    // Warm the screen-usage cache on entering this surface, then read it.
    prefetchProcessUsedActionIds(this.props.sd?.project?.id);
    this.loadUsedActions();
  }

  // Actions "used in a process" (workflow step actions ∪ user-task screen actions) → drives the
  // editor warning. See getProcessUsedActionIds (KOA-18085).
  loadUsedActions = async (): Promise<void> => {
    const usedActionIds: Set<string> = await getProcessUsedActionIds(
      this.props.sd?.workFlowList,
      this.props.sd?.project?.id
    );
    const usedActions: IActionDefinitionDto[] = (this.props.sd?.actionDefinitions ?? []).filter((a) =>
      usedActionIds.has((a.id ?? "").toLowerCase())
    );
    this.setState({ usedActions });
  };

  showNotification = () => {
    const { editingAction, activeEditingDatasource } = this.props.sd;

    if (editingAction) {
      const action: IActionDefinitionDto | undefined = this.state.usedActions.find((a) => a.id === editingAction.id);
      if (!action) return null;
      return this.renderProcessUsageNotice(
        `action:${editingAction.id}`,
        "Action is used in a process",
        <>
          Making changes to this action may affect older versions of that process. If you need to change the action,
          please consider creating a new one.
        </>
      );
    }

    if (activeEditingDatasource) {
      const actions: IActionDefinitionDto[] = this.state.usedActions.filter(
        (a) => a.kDatasource_Id === activeEditingDatasource.id
      );
      if (actions.length === 0) return null;
      return this.renderProcessUsageNotice(
        `datasource:${activeEditingDatasource.id}`,
        "Some actions are used in a process",
        <>
          Making changes to these actions may affect older versions of that process. If you need to change the action,
          please consider creating a new one.
          <ul>
            {actions.map((a) => (
              <li key={a.id}>{a.name}</li>
            ))}
          </ul>
        </>
      );
    }

    return null;
  };

  renderProcessUsageNotice = (noticeKey: string, title: string, description: React.ReactNode) => {
    if (this.state.dismissedNoticeKey === noticeKey) return null;
    return (
      <NoticeBanner
        severity="warning"
        badge="IN USE"
        title={title}
        description={description}
        onClose={() => this.setState({ dismissedNoticeKey: noticeKey })}
        flush
      />
    );
  };

  /**
   * Whether any datasource editor is on screen. It gates two things that must agree: whether the
   * editor layer renders at all, and whether the centre below it is hidden. They used to be
   * written out separately and had drifted — the editor list grew to eight flags while the hide
   * still tested three, so opening the column editor, the row browser or the session-variable
   * editor left the centre rendering underneath it.
   */
  isEditorOpen = (): boolean =>
    !!(
      this.props.sd?.editingTable ||
      this.props.sd?.creatingTablesDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingAction ||
      this.props.sd?.creatingActionsDatasource ||
      this.props.sd?.activeEditingDatasource !== undefined ||
      this.props.sd?.editingSessionVariables ||
      this.props.sd?.browsingRowsTable
    );

  render() {
    return (
      <>
        {this.props.sd &&
          (this.props.sd?.dashboardState === DashboardState.dataDiagramScheme ||
            this.props.sd?.dashboardState === DashboardState.aiHub) && (
            <>
              {/* Nothing here waits on `databaseDiagramData` any more. AI Functions never read it,
                  and the Datasources landing does not either — only the ER diagram does, so the
                  wait moved next to the diagram itself (`datasources-landing.tsx`). Gating the
                  whole canvas on it blanked both tools for as long as a call they do not use
                  took to return. */}
              {this.isEditorOpen() && (
                <div className="kuika_datasource">
                  {this.showNotification()}
                  <div
                    className="kuika_datasource-functions__wrapper"
                    style={{
                      overflow:
                        this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.RestApiWOSwagger &&
                        this.props.sd.activeEditingDatasourceIndex !== undefined
                          ? "hidden"
                          : ""
                    }}
                  >
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.MsSql ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.MsSql ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.CustomDb) && (
                      <CreateConnectionMssql editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined} />
                    )}

                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.Postgre ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.Postgre) && (
                      <CreateConnectionPostgresql
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.Oracle ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.Oracle) && (
                      <CreateConnectionOracle editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined} />
                    )}

                    {((this.props.sd?.activeCreatingDatasourceType &&
                      this.props.sd?.activeCreatingDatasourceType === EDatasourceType.CSharpFunctions) ||
                      (this.props.sd?.activeEditingDatasource?.datasourceType &&
                        (this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.CSharpFunctions ||
                          this.props.sd?.activeEditingDatasource?.datasourceType ===
                            EDatasourceType.CustomAuthenticationProvider ||
                          this.props.sd?.activeEditingDatasource?.datasourceType ===
                            EDatasourceType.CustomAuthorizationProvider ||
                          this.props.sd?.activeEditingDatasource?.datasourceType ===
                            EDatasourceType.CustomVerificationCodeSenderProvider ||
                          this.props.sd?.activeEditingDatasource?.datasourceType ===
                            EDatasourceType.CustomConnectionStringProvider ||
                          this.props.sd?.activeEditingDatasource?.datasourceType ===
                            EDatasourceType.CustomExternalTokenValidatorProvider ||
                          this.props.sd?.activeEditingDatasource?.datasourceType ===
                            EDatasourceType.CustomMailProvider))) && (
                      <CreateConnectionCsharp
                        key={
                          this.props.sd?.activeEditingDatasource?.id ??
                          `new:${this.props.sd?.activeCreatingDatasourceName ?? ""}`
                        }
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.RestApi ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.RestApi) && (
                      <CreateConnectionRestapi
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {/* Each editor is keyed by the record it holds. Without it an editor
                        asked to open another record sometimes kept its instance (a click's
                        two state writes are batched) and sometimes remounted (a write after
                        an await is not), so what survived the switch depended on the route:
                        Blockly showed the previous action's blocks, the table editor kept
                        deleted columns, and the SAP editor ignored its new id outright. */}
                    {(this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.ManagedDb ||
                      this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.ManagedDbOracle ||
                      this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.Oracle ||
                      this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.Postgre ||
                      this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.ManagedDbPostgre ||
                      this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.CustomDb) &&
                      (this.props.sd?.editingAction?.actionType === EDatasourceType.ManagedDb ||
                        this.props.sd?.editingAction?.actionType === EDatasourceType.ManagedDbOracle ||
                        this.props.sd.editingAction?.actionType === EDatasourceType.CustomDb ||
                        this.props.sd.editingAction?.actionType === EDatasourceType.Oracle ||
                        this.props.sd.editingAction?.actionType === EDatasourceType.Postgre ||
                        this.props.sd.editingAction?.actionType === EDatasourceType.ManagedDbPostgre ||
                        this.props.sd.activeCreatingActionName) && (
                        <CreateAction
                          key={
                            this.props.sd?.editingAction?.id ?? `new:${this.props.sd.activeCreatingActionName ?? ""}`
                          }
                          datasource={this.props.sd?.creatingActionsDatasource}
                          actionToEdit={this.props.sd?.editingAction ? this.props.sd?.editingAction : undefined}
                        />
                      )}
                    {this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.AIActions &&
                      (this.props.sd?.editingAction?.actionType === EDatasourceType.AIActions ||
                        this.props.sd.editingAction?.aiActionInfo ||
                        this.props.sd.activeCreatingActionName) && (
                        <CreateAiAction
                          actionDatasource={this.props.sd.creatingActionsDatasource}
                          actionToEdit={this.props.sd?.editingAction}
                        />
                      )}
                    {this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.SAP &&
                      (this.props.sd?.editingAction?.actionType === EDatasourceType.SAP ? (
                        <EditSapAction
                          key={
                            this.props.sd?.editingAction?.id ?? `new:${this.props.sd.activeCreatingActionName ?? ""}`
                          }
                          actionDatasource={this.props.sd.creatingActionsDatasource}
                          actionId={this.props.sd?.editingAction?.id}
                        />
                      ) : this.props.sd.activeCreatingActionName ? (
                        <CreateSapAction
                          actionDatasource={this.props.sd.creatingActionsDatasource}
                          actionToEdit={undefined}
                        />
                      ) : null)}
                    {this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.Blockly &&
                      (this.props.sd?.editingAction?.actionType === EDatasourceType.Blockly ||
                        this.props.sd.activeCreatingActionName) && (
                        <CreateBlocklyAction
                          key={
                            this.props.sd?.editingAction?.id ?? `new:${this.props.sd.activeCreatingActionName ?? ""}`
                          }
                          actionDatasource={this.props.sd.creatingActionsDatasource}
                          actionToEdit={this.props.sd?.editingAction ? this.props.sd?.editingAction : undefined}
                        />
                      )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.JavaScriptAction ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.JavaScriptAction) && (
                      <CreateJavaScriptAction
                        key={
                          this.props.sd?.activeEditingDatasource?.id ??
                          `new:${this.props.sd?.activeCreatingDatasourceName ?? ""}`
                        }
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.PythonAction ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.PythonAction) && (
                      <CreatePythonAction
                        key={
                          this.props.sd?.activeEditingDatasource?.id ??
                          `new:${this.props.sd?.activeCreatingDatasourceName ?? ""}`
                        }
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.JavaAction ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.JavaAction) && (
                      <CreateJavaAction
                        key={
                          this.props.sd?.activeEditingDatasource?.id ??
                          `new:${this.props.sd?.activeCreatingDatasourceName ?? ""}`
                        }
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.GoAction ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.GoAction) && (
                      <CreateGoAction
                        key={
                          this.props.sd?.activeEditingDatasource?.id ??
                          `new:${this.props.sd?.activeCreatingDatasourceName ?? ""}`
                        }
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.RustAction ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.RustAction) && (
                      <CreateRustAction
                        key={
                          this.props.sd?.activeEditingDatasource?.id ??
                          `new:${this.props.sd?.activeCreatingDatasourceName ?? ""}`
                        }
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.AIAgent &&
                      (this.props.sd?.editingAction?.actionType === EDatasourceType.AIAgent ||
                        this.props.sd.activeCreatingActionName) && (
                        <CreateAIAgentAction
                          actionDatasource={this.props.sd.creatingActionsDatasource}
                          actionToEdit={this.props.sd?.editingAction ? this.props.sd?.editingAction : undefined}
                        />
                      )}
                    {this.props.sd.creatingActionsDatasource?.datasourceType === EDatasourceType.VisionAction &&
                      (this.props.sd?.editingAction?.actionType === EDatasourceType.VisionAction ||
                        this.props.sd.activeCreatingActionName) && (
                        <CreateVisionAction
                          actionDatasource={this.props.sd.creatingActionsDatasource}
                          actionToEdit={this.props.sd?.editingAction ? this.props.sd?.editingAction : undefined}
                        />
                      )}
                    {(this.props.sd?.editingTable || this.props.sd.activeCreatingTableName) && (
                      <CreateTable
                        key={
                          this.props.sd?.editingTable?.id ||
                          this.props.sd?.editingTable?.name ||
                          `new:${this.props.sd?.activeCreatingTableName ?? ""}`
                        }
                        datasource={this.props.sd?.creatingTablesDatasource}
                        tableData={this.props.sd?.editingTable ? this.props.sd?.editingTable : undefined}
                        // Which tab Browse Data asked for. Not part of the key: a request for the table
                        // already open switches its tab, it does not rebuild the editor.
                        view={this.props.sd?.tableEditorView}
                      />
                    )}
                    {/* The fallback for tables the table editor cannot host (not managed, or no loaded
                        model with columns): `setBrowsingRowsTable` routes everything else to the
                        editor's Data tab. */}
                    {this.props.sd?.browsingRowsTable && (
                      <TableRowsEditor
                        // key includes the initial filter so an FK
                        // "go to" jump re-mounts the editor at the
                        // referenced row instead of stale state.
                        // Empty schema stays empty in the key so a
                        // missing schema (Oracle / Postgres no-schema)
                        // remains distinct from a literal "dbo"
                        // table — matches the storage-key contract
                        // in view-storage.ts.
                        key={`${this.props.sd.browsingRowsTable.schema ?? ""}.${this.props.sd.browsingRowsTable.name}.${this.props.sd.browsingRowsTable._initialFilter?.column ?? ""}=${this.props.sd.browsingRowsTable._initialFilter?.value ?? ""}`}
                        tableName={this.props.sd.browsingRowsTable.name}
                        tableSchema={this.props.sd.browsingRowsTable.schema}
                        sqlDialect={resolveTableEditorSqlDialect(this.props.sd?.browsingRowsDatasource?.datasourceType)}
                        datasourceId={this.props.sd?.browsingRowsDatasource?.id}
                        datasourceName={
                          this.props.sd?.browsingRowsDatasource
                            ? getDatasourceDisplayName(this.props.sd.browsingRowsDatasource)
                            : undefined
                        }
                        initialFilter={this.props.sd.browsingRowsTable._initialFilter}
                        switcherItems={buildRowBrowserSwitcherItems(
                          this.props.sd?.schemas?.get(this.props.sd?.browsingRowsDatasource?.id ?? "")?.tables ?? []
                        )}
                        onSwitchTable={(item: ITableRowsSwitcherItem) =>
                          this.props.sd?.setBrowsingRowsTable(this.props.sd?.browsingRowsDatasource, {
                            name: item.tableName,
                            schema: item.schema
                          })
                        }
                        onClose={() => this.props.sd?.setBrowsingRowsTable(undefined, undefined)}
                        onNavigateToTable={(target, initialFilter) => {
                          // The screen-design context tracks the
                          // active managed-db datasource; reuse it
                          // for the navigated-to table. Pass schema
                          // through verbatim — same no-dbo-coercion
                          // rule as above.
                          this.props.sd?.setBrowsingRowsTable(this.props.sd?.browsingRowsDatasource, {
                            name: target.name,
                            schema: target.schema,
                            _initialFilter: initialFilter
                          });
                        }}
                      />
                    )}
                    {this.props.sd.editingSessionVariables && <CreateSessionVariable />}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.RestApiWOSwagger ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.RestApiWOSwagger) && (
                      <>
                        {this.props.sd.activeEditingDatasource &&
                        this.props.sd.activeEditingDatasourceIndex !== undefined ? (
                          <CreateRestApiEndpoint
                            editingDatasource={this.props.sd.activeEditingDatasource}
                            editingEndpointIndex={this.props.sd.activeEditingDatasourceIndex}
                          />
                        ) : (
                          <CreateRestApiCollection
                            editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                          />
                        )}
                      </>
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.BleService ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.BleService) && (
                      <CreateConnectionBluetooth
                        editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined}
                      />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.Mqtt ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.Mqtt) && (
                      <CreateConnectionMqtt editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined} />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.OData ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.OData) && (
                      <CreateConnectionOData editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined} />
                    )}
                    {(this.props.sd?.activeCreatingDatasourceType === EDatasourceType.SAP ||
                      this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.SAP) && (
                      <CreateConnectionSAP editingDatasource={this.props.sd?.activeEditingDatasource ?? undefined} />
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  height: "calc(100vh - 32px - 49px)",
                  display: this.isEditorOpen() ? "none" : "",
                  overflow: "hidden"
                }}
              >
                {this.getCenter()}
              </div>
            </>
          )}
      </>
    );
  }

  getCenter = () => {
    if (this.props.sd?.dashboardState === DashboardState.aiHub) {
      return <AiHub />;
    }
    return <DatasourcesLanding />;
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const dataDiagramCanvas = withSD<IDataDiagramCanvasProps>(DataDiagramCanvas);
export { dataDiagramCanvas as DataDiagramCanvas };
